import { geocodeLocationRest, formatUsAddress } from '../../shared/geocodeLocation.mjs';
import { getGoogleMapsApiKey } from './maps/googleMapsEnv';
import { geocodeCityStateWithMapsJs, isGoogleMapsGeocoderReady } from './maps/mapsJsGeocoder';
import { parseCoordinatesFromText } from './geocoding';

export type GeocodeSuccess = { ok: true; lat: number; lng: number };
export type GeocodeFailure = { ok: false; reason: string };
export type GeocodeResult = GeocodeSuccess | GeocodeFailure;

export { formatUsAddress };

/**
 * Geocode a free-form US address string.
 * Browser: prefers Maps JS Geocoder (referrer-restricted keys), then REST.
 * Node/scripts: REST only (pass apiKey).
 */
export async function geocodeLocation(
  address: string,
  options?: { apiKey?: string; preferMapsJs?: boolean }
): Promise<GeocodeResult> {
  const trimmed = address.trim();
  if (!trimmed) return { ok: false, reason: 'empty_address' };

  const fromCoords = parseCoordinatesFromText(trimmed);
  if (fromCoords) return { ok: true, lat: fromCoords.lat, lng: fromCoords.lng };

  const preferMapsJs = options?.preferMapsJs !== false;
  if (preferMapsJs && isGoogleMapsGeocoderReady()) {
    const parts = trimmed.split(',').map((s) => s.trim());
    const city = parts[0] || '';
    const state = parts.length > 1 ? parts[1].replace(/\s*USA$/i, '').trim() : '';
    const coords = await geocodeCityStateWithMapsJs(city, state || city);
    if (coords) return { ok: true, lat: coords.lat, lng: coords.lng };
  }

  const apiKey = options?.apiKey ?? getGoogleMapsApiKey();
  if (!apiKey) return { ok: false, reason: 'missing_api_key' };
  return geocodeLocationRest(trimmed, apiKey) as Promise<GeocodeResult>;
}

export async function geocodeCityState(city: string, state: string): Promise<GeocodeResult> {
  return geocodeLocation(formatUsAddress({ city, state }));
}
